import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

export default function BlogsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/blogs"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { title: open.blog.title, slug: open.blog.slug || "", excerpt: open.blog.excerpt || "", content: open.blog.content || "", cover_image: open.blog.cover_image || "", tags: (open.blog.tags || []), published: !!open.blog.published };
      if (open.mode === "new") await api.post("/admin/blogs", payload);
      else await api.put(`/admin/blogs/${open.blog.blog_id}`, payload);
      toast.success("Saved"); setOpen(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this post?")) return; await api.delete(`/admin/blogs/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Blog</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} posts · shown as a slider on the home page.</p>
        </div>
        <Button onClick={() => setOpen({ mode: "new", blog: { title: "", excerpt: "", content: "", tags: [], published: true } })} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="blog-add-btn"><Plus className="h-4 w-4 mr-2" />New post</Button>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-3">Title</th><th className="p-3">Tags</th><th className="p-3">Published</th><th className="p-3 text-right"></th></tr>
          </thead>
          <tbody>
            {items.map(b => (
              <tr key={b.blog_id} className="border-t border-border">
                <td className="p-3"><div className="font-medium">{b.title}</div><div className="text-xs text-muted-foreground truncate max-w-xl">{b.excerpt}</div></td>
                <td className="p-3"><div className="flex flex-wrap gap-1">{(b.tags || []).map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}</div></td>
                <td className="p-3">{b.published ? <Badge className="bg-emerald-500/15 text-emerald-600 border-0">Live</Badge> : <Badge variant="outline">Draft</Badge>}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setOpen({ mode: "edit", blog: b })}><Edit className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(b.blog_id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.mode === "new" ? "New post" : "Edit post"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={open.blog.title} onChange={(e) => setOpen({ ...open, blog: { ...open.blog, title: e.target.value } })} data-testid="blog-title" /></div>
                <div><Label>Slug (URL)</Label><Input value={open.blog.slug || ""} onChange={(e) => setOpen({ ...open, blog: { ...open.blog, slug: e.target.value } })} placeholder="auto-generated if empty" /></div>
                <div><Label>Excerpt</Label><Textarea rows={2} value={open.blog.excerpt || ""} onChange={(e) => setOpen({ ...open, blog: { ...open.blog, excerpt: e.target.value } })} /></div>
                <div><Label>Content</Label><Textarea rows={8} value={open.blog.content || ""} onChange={(e) => setOpen({ ...open, blog: { ...open.blog, content: e.target.value } })} /></div>
                <div><Label>Tags (comma-separated)</Label><Input value={(open.blog.tags || []).join(", ")} onChange={(e) => setOpen({ ...open, blog: { ...open.blog, tags: e.target.value.split(",").map(x => x.trim()).filter(Boolean) } })} /></div>
                <div><Label>Cover image URL</Label><Input value={open.blog.cover_image || ""} onChange={(e) => setOpen({ ...open, blog: { ...open.blog, cover_image: e.target.value } })} /></div>
                <div className="flex items-center gap-2"><Switch checked={!!open.blog.published} onCheckedChange={(v) => setOpen({ ...open, blog: { ...open.blog, published: v } })} /><Label>Published</Label></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button><Button onClick={save} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="blog-save-btn">Save</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
